import { FC, useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import QrScanner from 'qr-scanner';
import { colors, fontWeights } from '../../theme/tokens';
import { useCameraStream, CameraError } from './useCameraStream';
import { usePinchZoom } from './usePinchZoom';
import { captureFrame } from './captureFrame';

// Capture of a page with the camera.
//
// It gathers what the former Archive page did inline: the <video> with the
// MediaStream (useCameraStream), the touch pinch-zoom (usePinchZoom), the
// live QR reader (qr-scanner) and the shot into a JPEG File
// (captureFrame). The XD does not draw this screen (DESIGN.md, gap 5:
// scanning is kept by the client's decision); it follows the pattern of the
// XD modals, in blue, with the video full-bleed.
//
// The QR is read here only as framing aid: the one that decides which code
// the page carries is the backend, which reads it from each capture when it
// is uploaded. That is why the component does not return the QR text.
//
// The smartphone XD (S4 in DESIGN.md) draws the aid as a viewfinder: an
// instruction in orange, the four edges of the frame and a central target
// that turn green when the shot is ready. "Ready" here means the live QR
// reader sees a code — the only alignment signal the app has; a page
// without a QR simply keeps the white frame and can be shot anyway.
//
// Mobile: full-screen sheet, rear camera, video in `cover` and the
// capture cropped to what is visible. Desktop: centred modal, full video
// (`contain`), no cropping.

type Props = {
  open: boolean;
  onClose: () => void;
  onCapture: (file: File) => void;
};

// The <video> is mounted behind the Dialog transition; a few frames is far more
// than it takes, and it bounds the wait rather than spinning forever.
const VIDEO_MOUNT_ATTEMPTS = 30;

// S4 turns the viewfinder green; the palette has no green token — this is
// the viewfinder semantic colour, not a brand colour.
const ALIGNED_GREEN = '#43A047';

const ERROR_TEXT: Record<CameraError, string> = {
  denied:
    "L'accès à la caméra a été refusé. Autorisez-le dans les réglages du navigateur, ou sélectionnez un fichier.",
  unavailable:
    'Aucune caméra disponible sur cet appareil. Sélectionnez un fichier à la place.',
  unknown:
    "La caméra n'a pas pu démarrer. Réessayez, ou sélectionnez un fichier.",
};

const CameraCaptureDialog: FC<Props> = ({ open, onClose, onCapture }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('tablet'));
  const videoRef = useRef<HTMLVideoElement>(null);
  const scannerRef = useRef<QrScanner | null>(null);
  const { stream, error, start, stop } = useCameraStream(videoRef);
  const [qrFound, setQrFound] = useState(false);
  const [bootFailed, setBootFailed] = useState(false);
  const [capturing, setCapturing] = useState(false);

  usePinchZoom(videoRef, open && isMobile);

  const teardown = useCallback(() => {
    if (scannerRef.current) {
      // `destroy` stops the stream the video has set; the camera is
      // closed along with it.
      scannerRef.current.destroy();
      scannerRef.current = null;
    }
    stop();
    setQrFound(false);
    setBootFailed(false);
  }, [stop]);

  // Boot on open. The Dialog mounts the <video> behind a transition, so the ref
  // is not necessarily set on the commit that opens it: giving up on a ref that
  // is merely late left the spinner turning with no camera and no error, which
  // is what a slower phone hit. It is waited for over a few frames instead.
  useEffect(() => {
    if (!open) return;

    let cancelled = false;

    const awaitVideo = async (): Promise<HTMLVideoElement | null> => {
      for (let attempt = 0; attempt < VIDEO_MOUNT_ATTEMPTS; attempt += 1) {
        if (cancelled) return null;
        if (videoRef.current) return videoRef.current;
        await new Promise<void>((resolve) => {
          requestAnimationFrame(() => resolve());
        });
      }
      return videoRef.current;
    };

    const boot = async () => {
      const video = await awaitVideo();
      if (cancelled) return;

      // Still nothing to draw into: say so rather than spin forever.
      if (!video) {
        setBootFailed(true);
        return;
      }

      const opened = await start(isMobile);
      if (cancelled || !opened) return;

      const scanner = new QrScanner(
        video,
        () => {
          setQrFound(true);
        },
        {
          returnDetailedScanResult: true,
          highlightScanRegion: false,
          highlightCodeOutline: false,
          maxScansPerSecond: 5,
        },
      );
      scannerRef.current = scanner;
      // `start` reuses the stream the <video> already has: it does not open another.
      scanner.start().catch(() => {});
    };

    boot();

    return () => {
      cancelled = true;
      teardown();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const close = () => {
    teardown();
    onClose();
  };

  const capture = async () => {
    const video = videoRef.current;
    if (!video || capturing) return;

    setCapturing(true);
    try {
      // The frame is drawn before stopping anything: the reader and the camera
      // are closed in `close`, once the file exists.
      const file = await captureFrame(video, { cover: isMobile });
      if (file) {
        onCapture(file);
        close();
      }
    } finally {
      setCapturing(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      fullScreen={isMobile}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: colors.primary,
          backgroundImage: 'none',
          color: colors.white,
          overflow: 'hidden',
        },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: isMobile ? '100%' : '70vh',
          minHeight: isMobile ? '100vh' : 360,
          backgroundColor: '#000',
        }}
      >
        <video
          ref={videoRef}
          id="camera-preview"
          autoPlay
          muted
          playsInline
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: isMobile ? 'cover' : 'contain',
            display: error || bootFailed ? 'none' : 'block',
          }}
        />

        {/* Top bar: title and ✕, as in the XD modals */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1,
            background:
              'linear-gradient(rgba(0, 0, 0, 0.55), rgba(0, 0, 0, 0))',
            zIndex: 2,
          }}
        >
          <Typography
            component="h2"
            sx={{ fontWeight: fontWeights.semiBold, color: colors.white }}
          >
            Prendre une photo
          </Typography>
          {/* S4: the ✕ is orange, as in every modal. */}
          <IconButton
            aria-label="Fermer"
            onClick={close}
            sx={{ color: colors.accent }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* S4 instruction, over the viewfinder */}
        {stream && !error && (
          <Typography
            sx={{
              position: 'absolute',
              top: 52,
              left: 16,
              right: 16,
              textAlign: 'center',
              color: colors.accentLight,
              fontWeight: fontWeights.semiBold,
              fontSize: '0.8125rem',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.6)',
              zIndex: 2,
            }}
          >
            Prenez la photo quand les 4 bords et le viseur central sont verts
          </Typography>
        )}

        {/* S4 viewfinder: frame edges and central target, green when the
            live reader sees a QR. */}
        {stream && !error && (
          <Box aria-hidden sx={{ position: 'absolute', inset: 0, zIndex: 1, pointerEvents: 'none' }}>
            <Box
              sx={{
                position: 'absolute',
                top: 88,
                left: 16,
                right: 16,
                bottom: isMobile ? 132 : 100,
                border: `3px solid ${qrFound ? ALIGNED_GREEN : 'rgba(255, 255, 255, 0.85)'}`,
                borderRadius: '6px',
                transition: 'border-color 150ms',
              }}
            />
            {[64, 32].map((size) => (
              <Box
                key={size}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  width: size,
                  height: size,
                  marginTop: `-${size / 2}px`,
                  marginLeft: `-${size / 2}px`,
                  borderRadius: '50%',
                  border: `3px solid ${qrFound ? ALIGNED_GREEN : 'rgba(255, 255, 255, 0.85)'}`,
                  transition: 'border-color 150ms',
                }}
              />
            ))}
          </Box>
        )}

        {/* What the colour says, for screen readers. */}
        <Box
          role="status"
          sx={{
            position: 'absolute',
            width: '1px',
            height: '1px',
            overflow: 'hidden',
            clip: 'rect(0 0 0 0)',
            whiteSpace: 'nowrap',
          }}
        >
          {qrFound ? 'QR code détecté' : ''}
        </Box>

        {/* Camera state */}
        {!stream && !error && !bootFailed && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress color="inherit" />
          </Box>
        )}

        {(error || bootFailed) && (
          <Box
            role="alert"
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              p: 3,
              textAlign: 'center',
              backgroundColor: colors.primary,
            }}
          >
            <Typography>
              {error ? ERROR_TEXT[error] : ERROR_TEXT.unknown}
            </Typography>
            <Button variant="outlined" color="inherit" onClick={close}>
              Fermer
            </Button>
          </Box>
        )}

        {/* Shutter */}
        {stream && (
          <Button
            id="capture-photo"
            aria-label="Capturer la page"
            onClick={capture}
            disabled={capturing}
            sx={{
              position: 'absolute',
              bottom: isMobile ? 48 : 20,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 68,
              height: 68,
              minWidth: 0,
              p: 0,
              borderRadius: '50%',
              backgroundColor: colors.white,
              border: `4px solid ${colors.accent}`,
              zIndex: 2,
              '&:hover': { backgroundColor: colors.surface },
              '&.Mui-disabled': { backgroundColor: colors.border },
            }}
          />
        )}
      </Box>
    </Dialog>
  );
};

export default CameraCaptureDialog;
